// TipTap custom node extensions for LaTeX document structures
// Includes: document metadata, theorem environments, sections, etc.

import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { useState, useEffect } from "react";

// ── Document Metadata Nodes ──────────────────────────────────────────────────

// Document Title — appears centered, bold, uppercase
function DocTitleView({ node }: NodeViewProps) {
    return (
        <NodeViewWrapper data-type="doc-title" className="latex-doc-title">
            <h1 className="title-editable">
                <NodeViewContent />
            </h1>
        </NodeViewWrapper>
    );
}

export const DocTitle = Node.create({
    name: "docTitle",
    group: "block",
    content: "inline*",
    atom: false,
    draggable: false,

    parseHTML() {
        return [{ tag: 'div[data-type="doc-title"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "doc-title" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(DocTitleView);
    },
});

// Document Author(s) — appears centered below title
function DocAuthorView({ node }: NodeViewProps) {
    return (
        <NodeViewWrapper data-type="doc-author" className="latex-doc-author">
            <div className="author-editable">
                <NodeViewContent />
            </div>
        </NodeViewWrapper>
    );
}

export const DocAuthor = Node.create({
    name: "docAuthor",
    group: "block",
    content: "inline*",
    atom: false,
    draggable: false,

    parseHTML() {
        return [{ tag: 'div[data-type="doc-author"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "doc-author" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(DocAuthorView);
    },
});

// Document Date — appears centered below the author line
function DocDateView() {
    return (
        <NodeViewWrapper data-type="doc-date" className="latex-doc-date">
            <div className="date-editable">
                <NodeViewContent />
            </div>
        </NodeViewWrapper>
    );
}

export const DocDate = Node.create({
    name: "docDate",
    group: "block",
    content: "inline*",
    atom: false,
    draggable: false,

    parseHTML() {
        return [{ tag: 'div[data-type="doc-date"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "doc-date" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(DocDateView);
    },
});

// Abstract — block environment with "Abstract." prefix
function DocAbstractView({ node }: NodeViewProps) {
    return (
        <NodeViewWrapper data-type="doc-abstract" className="latex-abstract">
            <div className="abstract-header">Abstract.</div>
            <div className="abstract-body">
                <NodeViewContent />
            </div>
        </NodeViewWrapper>
    );
}

export const DocAbstract = Node.create({
    name: "docAbstract",
    group: "block",
    content: "block+",
    atom: false,
    draggable: false,

    parseHTML() {
        return [{ tag: 'div[data-type="doc-abstract"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "doc-abstract" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(DocAbstractView);
    },
});

// ── Table of Contents ─────────────────────────────────────────────────────────

function DocTocView({ editor }: NodeViewProps) {
    // Force re-render whenever the document changes so headings stay current
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!editor) return;
        const handler = () => setTick(t => t + 1);
        editor.on("update", handler);
        return () => { editor.off("update", handler); };
    }, [editor]);

    // Walk the live document to gather all heading nodes
    const entries: { level: number; text: string }[] = [];
    if (editor) {
        editor.state.doc.descendants((node) => {
            if (node.type.name === "heading") {
                const level: number = node.attrs.level ?? 1;
                const text = node.textContent;
                if (text) entries.push({ level, text });
            }
        });
    }

    // Section counters for numbering (1, 1.1, 1.1.1, ...)
    const counters = [0, 0, 0];
    const numbered = entries.map(({ level, text }) => {
        const idx = level - 1;
        counters[idx]++;
        for (let j = idx + 1; j < counters.length; j++) counters[j] = 0;
        const num = counters.slice(0, level).join(".");
        return { level, text, num };
    });

    return (
        <NodeViewWrapper data-type="doc-toc" className="latex-toc" contentEditable={false}>
            <div className="toc-header">Contents</div>
            <ol className="toc-list">
                {numbered.length === 0 ? (
                    <li className="toc-empty">No sections yet</li>
                ) : (
                    numbered.map((entry, i) => (
                        <li
                            key={i}
                            className={`toc-entry toc-level-${entry.level}`}
                            style={{ paddingLeft: `${(entry.level - 1) * 1.2}em` }}
                        >
                            <span className="toc-num">{entry.num}</span>
                            <span className="toc-title">{entry.text}</span>
                        </li>
                    ))
                )}
            </ol>
        </NodeViewWrapper>
    );
}

export const DocToc = Node.create({
    name: "docToc",
    group: "block",
    atom: true,
    draggable: false,

    parseHTML() {
        return [{ tag: 'div[data-type="doc-toc"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "doc-toc" })];
    },

    addNodeView() {
        return ReactNodeViewRenderer(DocTocView);
    },
});

// ── Theorem-like Environments ─────────────────────────────────────────────────

interface TheoremAttrs {
    envType: string;  // theorem, lemma, proposition, corollary, definition, remark, example, proof
    envName?: string; // optional [name]
}

function TheoremBlockView({ node, updateAttributes }: NodeViewProps) {
    const envType: string = node.attrs.envType || "theorem";
    const envName: string = node.attrs.envName || "";

    // Map env types to display labels
    const labels: Record<string, string> = {
        theorem: "Theorem",
        lemma: "Lemma",
        proposition: "Proposition",
        corollary: "Corollary",
        definition: "Definition",
        remark: "Remark",
        example: "Example",
        proof: "Proof",
        exercise: "Exercise",
    };

    const label = labels[envType] || envType;

    return (
        <NodeViewWrapper data-type="theorem-block" className={`latex-theorem latex-${envType}`}>
            <div className="theorem-header">
                <span className="theorem-label">{label}</span>
                {envName && <span className="theorem-name">({envName})</span>}
            </div>
            <div className="theorem-body">
                <NodeViewContent />
            </div>
        </NodeViewWrapper>
    );
}

export const TheoremBlock = Node.create({
    name: "theoremBlock",
    group: "block",
    content: "block+",
    atom: false,
    draggable: true,

    addAttributes() {
        return {
            envType: {
                default: "theorem",
            },
            envName: {
                default: "",
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="theorem-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "theorem-block" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(TheoremBlockView);
    },
});

// ── Generic Environment Node ─────────────────────────────────────────────────

interface GenericEnvAttrs {
    envName: string;
}

function GenericEnvView({ node, updateAttributes }: NodeViewProps) {
    const envName: string = node.attrs.envName || "environment";

    return (
        <NodeViewWrapper data-type="generic-env" className="latex-generic-env">
            <div className="env-header">
                <span className="env-label">\\begin{"{"}</span>
                <input
                    className="env-name-input"
                    value={envName}
                    onChange={(e) => updateAttributes({ envName: e.target.value })}
                    placeholder="envname"
                />
                <span className="env-label">{"}"}</span>
            </div>
            <div className="env-body">
                <NodeViewContent />
            </div>
            <div className="env-footer">
                <span className="env-label">\\end{"{"}{envName}{"}"}</span>
            </div>
        </NodeViewWrapper>
    );
}

export const GenericEnv = Node.create({
    name: "genericEnv",
    group: "block",
    content: "block+",
    atom: false,
    draggable: true,

    addAttributes() {
        return {
            envName: {
                default: "",
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="generic-env"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "generic-env" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(GenericEnvView);
    },
});

// ── Two-Column Layout ────────────────────────────────────────────────────────

function TwoColumnsView({ }: NodeViewProps) {
    return (
        <NodeViewWrapper data-type="two-columns" className="latex-two-columns">
            <div className="two-columns-label">⫾ Two-column layout</div>
            <div className="two-columns-body">
                <NodeViewContent />
            </div>
        </NodeViewWrapper>
    );
}

export const TwoColumns = Node.create({
    name: "twoColumns",
    group: "block",
    content: "block+",
    atom: false,
    draggable: true,

    parseHTML() {
        return [{ tag: 'div[data-type="two-columns"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "two-columns" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(TwoColumnsView);
    },
});
